import React, { useContext, useEffect } from 'react';
import {
  Button,
  CircularProgress,
  Theme,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { makeStyles } from 'tss-react/mui';

import { UserContext } from '../../../components/data/context';
import DueDiligenceDocuments from '../../../pages/lp/due-dilligence/DueDiligenceDocuments';
import { ApplicationState } from '../../../store';
import { select } from '../../../store/lps/actions';
import { LPState } from '../../../store/lps/types';
import { pathReplace, paths } from '../../../store/routePaths';

interface DueDiligencePageProps {
  id?: string;
  indicated?: boolean;
  committed: boolean;
  lpNumber?: string;
  match?: any;
}

const PurchaseStatusBanner = styled.div.withConfig({
  shouldForwardProp: prop => !['committed'].includes(prop),
})<DueDiligencePageProps>`
  position: fixed;
  width: 100%;
  bottom: 0;
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  -webkit-backdrop-filter: blur(30px);
  backdrop-filter: blur(30px);
  background: ${(props: any) => (!props.committed ? 'transparent' : 'auto')};
`;

const DueDiligencePage: React.FC<DueDiligencePageProps> = props => {
  const dispatch = useDispatch();
  const lp = useSelector<ApplicationState, LPState>(state => state.lp);
  const { id: lpNumber } = useParams();

  const { committed } = props;
  const { classes } = useStyles({ committed });
  const user = useContext(UserContext);

  useEffect(() => {
    dispatch(select(lpNumber));
  }, [dispatch, lpNumber]);

  if (user && !lp?.loading && !user?.loading && !user?.data?.Roles?.includes('mfa')) {
    return <Navigate to={`/api/auth/pii/lp/${lpNumber}`} />;
  }

  if (lp?.errors) {
    console.log('Errors');
    console.log(lp);
    return (
      <Typography className={classes.label}>The requested offering is not available.</Typography>
    );
  }

  let daysRemaining: any = undefined;
  const progress = (
    <div
      style={{
        minHeight: 70,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <CircularProgress size={24} color="secondary" />
    </div>
  );

  if (lp.lpData[lpNumber] && !lp?.loading) {
    daysRemaining = lp.lpData[lpNumber].DueDiligenceDaysRemaining;
  }

  return (
    <div className={classes.wrapper}>
      <section className={classes.root}>
        <div className={classes.number}>
          <div className={classes.numberWrapper}>
            <div style={{ position: 'relative' }}>
              <span>{daysRemaining === undefined ? progress : daysRemaining}</span>
              <div>DAYS LEFT</div>
            </div>
          </div>
        </div>
        <div>
          <h1 className={classes.title}>LOAN PARTICIPATION PORTFOLIO #{lpNumber}</h1>
          <p className={classes.descriptiveText}>
            Download any files you need for your records or additional research.
          </p>

          <section className={classes.dueDiligenceDocs}>
            <h3>Due Diligence Documents</h3>
            <DueDiligenceDocuments {...props} />
          </section>
        </div>
      </section>
      {!lp?.loading && !user?.loading && !user.admin && (
        <PurchaseStatusBanner committed={committed}>
          <PurchaseButton committed={committed} classes={classes} />
        </PurchaseStatusBanner>
      )}
    </div>
  );
};

export default DueDiligencePage;

const PurchaseButton = ({ committed, classes }) => {
  const { id: lpNumber } = useParams();
  const navigate = useNavigate();
  const handleClick = e => {
    navigate(pathReplace(paths.lpPurchaseCommitment, lpNumber));
  };
  if (committed) {
    return <div className={classes.submitButton}>Purchase Request Submitted</div>;
  } else {
    return (
      <Button
        color="primary"
        variant="contained"
        className={classes.submitButton}
        onClick={handleClick}
      >
        SUBMIT PURCHASE
      </Button>
    );
  }
};

const useStyles = makeStyles<{ committed?:boolean}>()((theme: Theme, props) =>
  ({
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      backgroundImage: `linear-gradient(232deg, ${theme.colors.darkSkyBlue}, #397cc6)`,
    },
    label: {
      textAlign: 'left',
      margin: '10px',
    },
    root: {
      flex: '1',
      display: 'flex',
      alignItems: 'flex-start',
      padding: '100px 150px 100px 100px',
      '& > div': {
        flex: '1 1 auto',
        '&:nth-of-type(1)': {
          flex: '0 0 auto',
        },
        '&:nth-of-type(2)': {
          marginLeft: 20,
          alignSelf: 'center',
        },
      },
    },
    number: {
      color: theme.colors.white,
      display: 'flex',
      justifyContent: 'flex-end',
      height: 100,
    },
    numberWrapper: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      borderRadius: 10,
      padding: '0 5px',
      backgroundColor: theme.colors.lipstick,
      width: 105,
      textAlign: 'center',
      fontStyle: 'italic',
      fontFamily: 'OpenSans',
      textShadow: '0 3px 6px rgba(0, 0, 0, 0.16)',
      fontSize: 16,
      '& span': {
        fontFamily: 'Montserrat',
        margin: 0,
        fontStyle: 'normal',
        fontSize: 49,
        fontWeight: 800,
      },
      '& > div > div': {
        marginTop: -10,
      },
    },
    title: {
      fontSize: 50,
      color: theme.colors.white,
      marginBottom: 0,
      marginTop: 0,
    },
    descriptiveText: {
      margin: 0,
      color: theme.colors.white,
      fontSize: 16,
      fontWeight: 600,
      textShadow: '0 3px 6px rgba(0, 0, 0, 0.16)',
    },
    dueDiligenceDocs: {
      marginTop: 100,
      minHeight: 500,
      '& h3': {
        textAlign: 'center',
        color: theme.colors.white,
        fontSize: 30,
        fontWeight: 600,
      },
    },
    submitButton: {
      flexBases: '100%',
      width: 'calc(100% - 240px)',
      height: 75,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      justifyItems: 'center',
      fontSize: 20,
      fontWeight: 600,
      color: (props.committed) ? theme.colors.navyBlue : theme.colors.white,
      textAlign: 'center',
      textShadow: '0 3px 10px rgba(0, 0, 0, 0.49)',
      '& span': {
        color: theme.colors.white,
      },
    },
  })
);
